import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UsersThree } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinTeam() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("register"); // "register" | "login"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/team/invite-info/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((err) => setError(err.response?.data?.detail || "Convite inválido"));
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/team/invite-link/${token}/accept`, {
        mode, name, email, password,
      });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      toast.success(`Bem-vindo(a) à equipe ${data.company_name}!`);
      window.location.href = "/";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao entrar na equipe");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-950">SOP & RAFT</h1>
          <p className="text-[10px] tracking-[0.15em] uppercase text-zinc-400 mt-1 font-semibold">Convite de Equipe</p>
        </div>
        <div className="bg-white rounded-sm border border-zinc-200 shadow-sm p-8" data-testid="join-team-card">
          {error ? (
            <div className="text-center space-y-3">
              <p className="text-red-600 font-semibold" data-testid="join-error">{error}</p>
              <Button variant="outline" onClick={() => (window.location.href = "/login")}>Ir para o login</Button>
            </div>
          ) : !info ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-zinc-900" />
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <UsersThree size={36} className="mx-auto text-zinc-700 mb-2" weight="duotone" />
                <p className="text-sm text-zinc-600">
                  Você foi convidado(a) para a equipe de
                </p>
                <p className="text-lg font-bold text-zinc-900" data-testid="join-company-name">{info.company_name}</p>
                <p className="text-xs text-zinc-400 mt-1">Nível de acesso: {info.role_label}</p>
              </div>

              <div className="flex border border-zinc-200 rounded-md overflow-hidden mb-5">
                <button
                  onClick={() => setMode("register")}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "register" ? "bg-zinc-950 text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
                  data-testid="join-mode-register"
                >
                  Criar conta
                </button>
                <button
                  onClick={() => setMode("login")}
                  className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "login" ? "bg-zinc-950 text-white" : "bg-white text-zinc-500 hover:bg-zinc-50"}`}
                  data-testid="join-mode-login"
                >
                  Já tenho conta
                </button>
              </div>

              <form onSubmit={handleAccept} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Seu nome</Label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="join-name" />
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" data-testid="join-email" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">
                    {mode === "register" ? "Crie sua senha" : "Sua senha"}
                  </Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" data-testid="join-password" />
                </div>
                {mode === "login" && (
                  <p className="text-xs text-zinc-500">
                    Sua conta será vinculada à equipe. Suas fichas e ingredientes atuais continuam seus, como itens pessoais.
                  </p>
                )}
                <Button type="submit" disabled={submitting} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="join-submit">
                  {submitting ? "Entrando..." : "Entrar na equipe"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
